import { fmtMoney } from "../OrgOverview/format";

export const INCOME_TYPES = [
  "Base",
  "Bonus",
  "Overtime",
  "Commission",
  "Self-Employment",
];

export const HUMAN_STATUSES = [
  "Qualifying",
  "Conditional",
  "Excluded",
  "Needs review",
];

export function getSuiteRunId(run) {
  return run?.suiteRunId || run?.id || "";
}

export function getRunLoans(run) {
  const candidates = [
    run?.loans,
    run?.results,
    run?.loanResults,
    run?.evaluations,
  ];

  return candidates.find(Array.isArray) || [];
}

export function getGoldenLoanId(loan) {
  return loan?.goldenLoanId || loan?.id || loan?.goldenLoan?.id || "";
}

export function getLoanName(loan) {
  return (
    loan?.borrowerName ||
    loan?.loan?.borrowerName ||
    loan?.goldenLoan?.borrowerName ||
    loan?.loanId ||
    getGoldenLoanId(loan) ||
    "Golden loan"
  );
}

export function getSourceAmount(source) {
  const direct = getSourceQualifyingAmount(source);
  if (direct != null) {
    return direct;
  }

  return sumComponentAmounts(source, "qualifying");
}

export function getSourceAvailableAmount(source) {
  const candidates = [
    source?.availableMonthlyIncome,
    source?.available,
    source?.loanTotals?.available,
    source?.normalized?.totals?.available,
  ];
  const amount = candidates.find((value) => typeof value === "number");
  if (typeof amount === "number") {
    return amount;
  }

  return sumComponentAmounts(source, "available");
}

export function getSourceQualifyingAmount(source) {
  const candidates = [
    source?.qualifyingMonthlyIncome,
    source?.monthlyIncome,
    source?.amount,
    source?.qualifying,
    source?.total,
    source?.loanTotals?.qualifying,
    source?.normalized?.totals?.qualifying,
  ];
  const amount = candidates.find((value) => typeof value === "number");
  if (typeof amount === "number") {
    return amount;
  }

  return null;
}

function sumComponentAmounts(source, metric) {
  const components = source?.normalized?.components || source?.components;
  if (!Array.isArray(components) || components.length === 0) {
    return null;
  }

  let total = 0;
  let hasValue = false;
  components.forEach((component) => {
    const componentAmount =
      metric === "available"
        ? component.availableAmount
        : component.amount ?? component.qualifyingMonthlyIncome;
    if (typeof componentAmount === "number" && Number.isFinite(componentAmount)) {
      total += componentAmount;
      hasValue = true;
    }
  });

  return hasValue ? Math.round(total * 100) / 100 : null;
}

export function formatMoneyPairText(available, qualifying) {
  return `Avail ${formatEvalMoney(available)} · Qual ${formatEvalMoney(qualifying)}`;
}

export function getHumanAmount(loan) {
  const human = loan?.human || loan?.humanLabel;
  const direct = getSourceAmount(human);
  if (direct != null) {
    return direct;
  }
  const rows = human?.rows;
  if (!Array.isArray(rows) || rows.length === 0) {
    return null;
  }

  return rows.reduce((total, row) => {
    const amount = Number(row.amount);

    return Number.isFinite(amount) ? total + amount : total;
  }, 0);
}

export function getJudgeVerdict(loan) {
  const judge = loan?.judge || loan?.review || loan?.reviewer;
  const agreement = loan?.evaluation?.addyVsHarness?.agreement;

  return (
    judge?.verdict ||
    judge?.status ||
    loan?.judgeVerdict ||
    loan?.verdict ||
    (agreement === true
      ? "Agrees"
      : agreement === false
        ? "Differences found"
        : null) ||
    "Pending"
  );
}

export function getDifferences(loan) {
  const candidates = [
    loan?.differences,
    loan?.diffs,
    loan?.comparison?.differences,
    loan?.judge?.differences,
    loan?.evaluation?.addyVsHarness?.differences,
  ];
  const differences = candidates.find(Array.isArray);

  return differences || [];
}

export function getDifferencePath(difference, index = 0) {
  const base =
    difference?.path ||
    difference?.field ||
    difference?.label ||
    `difference-${index + 1}`;
  if (!difference?.metric || difference.metric === "qualifying") {
    return base;
  }

  return `${base}#${difference.metric}`;
}

export function getDifferenceSummary(
  difference,
  leftSource = "addy",
  rightSource = "harness",
) {
  if (typeof difference === "string") {
    return difference;
  }

  const summary =
    difference?.summary || difference?.message || difference?.description;
  if (summary) {
    return summary;
  }
  if (difference?.left != null || difference?.right != null) {
    const metricLabel =
      difference?.metric === "available" ? "available" : "qualifying";

    return `${getDifferencePath(difference)} (${metricLabel}): ${sourceLabel(
      leftSource,
    )} ${formatEvalMoney(
      difference.left,
    )}, ${sourceLabel(rightSource)} ${formatEvalMoney(difference.right)}`;
  }

  return getDifferencePath(difference);
}

function sourceLabel(source) {
  const normalized = String(source || "").toLowerCase();
  if (normalized === "human") {
    return "Human";
  }
  if (normalized === "addy") {
    return "Addy";
  }
  if (normalized === "harness") {
    return "Harness";
  }

  return source || "Source";
}

export function formatEvalMoney(value) {
  return typeof value === "number" ? fmtMoney(value) : "—";
}

export function formatDuration(value) {
  if (typeof value !== "number") {
    return "—";
  }
  if (value < 1000) {
    return `${Math.round(value)} ms`;
  }
  if (value < 60000) {
    return `${(value / 1000).toFixed(value < 10000 ? 1 : 0)} s`;
  }

  const minutes = Math.floor(value / 60000);
  const seconds = Math.round((value % 60000) / 1000);

  return `${minutes}m ${seconds}s`;
}

export function formatRate(value) {
  if (typeof value !== "number") {
    return "—";
  }
  const percentage = value <= 1 ? value * 100 : value;

  return `${Math.round(percentage)}%`;
}

export function normalizeStatus(status) {
  return String(status || "queued")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-");
}

export function getRunMetric(run, keys, fallback = null) {
  for (const key of keys) {
    if (run?.[key] != null) {
      return run[key];
    }
    if (run?.summary?.[key] != null) {
      return run.summary[key];
    }
    if (run?.metrics?.[key] != null) {
      return run.metrics[key];
    }
  }

  return fallback;
}

export function createEmptyHumanRow(overrides = {}) {
  return {
    id: `human-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    path: "",
    borrowerId: "",
    borrowerName: "",
    employmentId: "",
    employmentName: "",
    businessId: "",
    businessName: "",
    incomeType: "Base",
    amount: "",
    status: "Qualifying",
    notes: "",
    ...overrides,
  };
}

export function normalizeHumanLabel(label) {
  const sourceRows = label?.rows || label?.components;
  if (!sourceRows?.length) {
    return { rows: [createEmptyHumanRow()], notes: label?.notes || "" };
  }

  return {
    ...label,
    rows: sourceRows.map((row) => {
      const [borrowerName = "", employmentName = "", incomeType = "Base"] =
        String(row.path || "").split("/");
      const amount = row.amount ?? row.qualifyingMonthlyIncome;

      return createEmptyHumanRow({
        ...row,
        borrowerName: row.borrowerName || borrowerName,
        employmentName:
          row.employmentName || row.businessName || employmentName,
        incomeType: row.incomeType || incomeType,
        amount: amount == null ? "" : String(amount),
        status: row.status || "Qualifying",
        notes: row.notes || "",
      });
    }),
  };
}

export function serializeHumanLabel(label) {
  const components = (label?.rows || [])
    .map((row) => {
      const amount = row.amount === "" ? null : Number(row.amount);
      const source =
        row.employmentName ||
        row.employmentId ||
        row.businessName ||
        row.businessId;
      const path =
        row.path ||
        [row.borrowerName || row.borrowerId, source, row.incomeType]
          .filter(Boolean)
          .join("/");

      return {
        path,
        amount,
        qualifyingMonthlyIncome: amount,
        status: row.status || null,
        notes: row.notes || null,
      };
    })
    .filter(
      (component) =>
        component.path.split("/").length >= 3 &&
        typeof component.amount === "number" &&
        Number.isFinite(component.amount),
    );

  return { components, notes: label?.notes || "" };
}

function addSourceRows(target, source, sourceKey) {
  if (!source) {
    return;
  }
  const normalizedSource = source.normalized || source;
  const flatRows = normalizedSource.rows || normalizedSource.components;
  if (Array.isArray(flatRows)) {
    flatRows.forEach((row, index) => {
      const path =
        row.path ||
        [
          row.borrowerId || row.borrowerName,
          row.employmentId ||
            row.employmentName ||
            row.businessId ||
            row.businessName,
          row.incomeType || row.type,
        ]
          .filter(Boolean)
          .join(" / ") ||
        `${sourceKey}-${index}`;
      target.set(path, {
        ...(target.get(path) || {}),
        path,
        employment:
          row.employmentName ||
          row.employmentId ||
          row.businessName ||
          row.businessId ||
          String(row.path || "").split("/")[1] ||
          "Income",
        incomeType:
          row.incomeType ||
          row.type ||
          String(row.path || "").split("/")[2] ||
          "Income",
        status: row.status,
        borrower:
          row.borrowerName ||
          row.borrowerId ||
          String(row.path || "").split("/")[0] ||
          "Borrower",
        [`${sourceKey}Status`]: row.status || null,
        [`${sourceKey}Detail`]: {
          formula: row.formula || null,
          decisionReason: row.decisionReason || null,
          documents: Array.isArray(row.documents) ? row.documents : [],
          sourceValues: Array.isArray(row.sourceValues) ? row.sourceValues : [],
          guidelineReference: row.guidelineReference || null,
          missingInputs: Array.isArray(row.missingInputs)
            ? row.missingInputs
            : [],
          ambiguityFlags: Array.isArray(row.ambiguityFlags)
            ? row.ambiguityFlags
            : [],
          rawIncomeType: row.rawIncomeType || null,
          calculatedAmount:
            typeof row.calculatedAmount === "number"
              ? row.calculatedAmount
              : null,
        },
        [`${sourceKey}Available`]: Number.isFinite(Number(row.availableAmount))
          ? Number(row.availableAmount)
          : null,
        [`${sourceKey}Qualifying`]: Number.isFinite(
          Number(row.amount ?? row.qualifyingMonthlyIncome),
        )
          ? Number(row.amount ?? row.qualifyingMonthlyIncome)
          : null,
        [sourceKey]: Number.isFinite(
          Number(row.amount ?? row.qualifyingMonthlyIncome),
        )
          ? Number(row.amount ?? row.qualifyingMonthlyIncome)
          : null,
      });
    });

    return;
  }

  (normalizedSource.borrowers || []).forEach((borrower, borrowerIndex) => {
    const borrowerName =
      borrower.borrowerName || borrower.name || `Borrower ${borrowerIndex + 1}`;
    (borrower.employments || []).forEach((employment, employmentIndex) => {
      Object.entries(employment.components || {}).forEach(
        ([type, component]) => {
          const path = `${borrower.borrowerId || borrowerName}/${
            employment.employmentId || employmentIndex
          }/${type}`;
          target.set(path, {
            ...(target.get(path) || {}),
            path,
            borrower: borrowerName,
            employment:
              employment.employerName ||
              employment.name ||
              `Employment ${employmentIndex + 1}`,
            incomeType: type,
            status: component?.status,
            [sourceKey]: getSourceAmount(component),
          });
        },
      );
    });
    (borrower.businesses || []).forEach((business, businessIndex) => {
      const path = `${borrower.borrowerId || borrowerName}/${
        business.businessId || businessIndex
      }/Self-Employment`;
      target.set(path, {
        ...(target.get(path) || {}),
        path,
        borrower: borrowerName,
        employment:
          business.businessName ||
          business.name ||
          `Business ${businessIndex + 1}`,
        incomeType: "Self-Employment",
        status: business.components?.SelfEmployment?.status,
        [sourceKey]: getSourceAmount(business.components?.SelfEmployment),
      });
    });
  });
}

export function getComponentRows(loan) {
  if (Array.isArray(loan?.components)) {
    return loan.components.map((row, index) => ({
      path: row.path || row.label || `component-${index}`,
      borrower: row.borrowerName || row.borrower || "Borrower",
      employment:
        row.employmentName || row.businessName || row.employment || "Income",
      incomeType: row.incomeType || row.type || row.label || "Income",
      status: row.status,
      human:
        typeof row.human === "number" ? row.human : getSourceAmount(row.human),
      humanAvailable:
        typeof row.humanAvailable === "number"
          ? row.humanAvailable
          : getSourceAvailableAmount(row.human),
      addy: typeof row.addy === "number" ? row.addy : getSourceAmount(row.addy),
      addyAvailable:
        typeof row.addyAvailable === "number"
          ? row.addyAvailable
          : getSourceAvailableAmount(row.addy),
      harness:
        typeof row.harness === "number"
          ? row.harness
          : getSourceAmount(row.harness),
      harnessAvailable:
        typeof row.harnessAvailable === "number"
          ? row.harnessAvailable
          : getSourceAvailableAmount(row.harness),
    }));
  }

  const rows = new Map();
  addSourceRows(rows, loan?.human || loan?.humanLabel, "human");
  addSourceRows(rows, loan?.addy, "addy");
  addSourceRows(rows, loan?.harness, "harness");

  return Array.from(rows.values()).map((row) => ({
    ...row,
    human: row.human ?? row.humanQualifying ?? null,
    humanAvailable: row.humanAvailable ?? null,
    addy: row.addy ?? row.addyQualifying ?? null,
    addyAvailable: row.addyAvailable ?? null,
    harness: row.harness ?? row.harnessQualifying ?? null,
    harnessAvailable: row.harnessAvailable ?? null,
  }));
}
